import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import classnames from 'clsx';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { Text, TextType, TextTypePropType } from 'ui-components/components/text';
import { noop } from 'ui-components/default-props/noop';

import { messages as translatedMessages } from './messages';
import { useOnElementResize } from '../../../hooks/use-on-element-resize';
import { MessageDescriptorPropType } from '../../../prop-types/intl';

import style from './expandable-message.module.less';

const LINE_HEIGHT_FALLBACK = 16;
const SCROLL_HEIGHT_DELTA_TOLERANCE = 1;

const getHeightOfElement = (element) => {
  const offsetHeight = element.offsetHeight;
  const scrollHeight = element.scrollHeight;

  // So this was a fun one to track down...
  // While doing some testing, I noticed that Firefox would sometimes show the expander button when it shouldn't.
  // After some digging, I noticed that the scrollHeight was 1px larger than the offsetHeight. This was causing the
  // line number calculation to be off and causing the button to show. When testing on Chrome, this did not happen, the
  // scrollHeight and offsetHeight were equal. I have 2 hunches.
  //  1. According to MDN, scrollHeight "... will round the value to an integer", so maybe there is a difference in how
  //  each browser rounds that value.
  //  2. Firefox and Chrome render fonts differently, so maybe the way Firefox renders it is causing it to go past the
  //  line height.
  //
  // To get around this issue, I'm checking if the delta between the scrollHeight and offsetHeight is within a tolerance
  // and if so, returning the offsetHeight instead.
  const delta = scrollHeight - offsetHeight;
  if (delta > SCROLL_HEIGHT_DELTA_TOLERANCE) {
    return scrollHeight;
  }

  return offsetHeight;
};

const getNumberOfLinesInElement = (element) => {
  const height = getHeightOfElement(element);
  const computedStyle = getComputedStyle(element);
  const lineHeight = parseInt(computedStyle.lineHeight, 10) || LINE_HEIGHT_FALLBACK;
  const padding = parseInt(computedStyle.paddingTop, 10) + parseInt(computedStyle.paddingBottom, 10) || 0;

  return Math.ceil((height - padding) / lineHeight);
};

const MessageText = ({ message, messageIndex, textType, onUpdateRef, isBold }) => {
  const updateMessagesRef = useCallback(
    (textRef) => {
      onUpdateRef(messageIndex, textRef);
    },
    [messageIndex, onUpdateRef],
  );

  return <Text isBold={isBold} ref={updateMessagesRef} textType={textType} value={message} />;
};

MessageText.propTypes = {
  message: PropTypes.string.isRequired,
  messageIndex: PropTypes.number.isRequired,
  textType: TextTypePropType.isRequired,
  onUpdateRef: PropTypes.func.isRequired,
  isBold: PropTypes.bool,
};

export const ExpandableMessage = ({
  messages,
  maxLines,
  expandMessageDescriptor = translatedMessages.viewMore,
  collapseMessageDescriptor = translatedMessages.viewLess,
  className,
  textType = TextType.Paragraph,
  onToggleExpander = noop,
  buttonClassName,
  isBold,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showExpanderButton, setShowExpanderButton] = useState(false);
  const truncatedTextContainerRef = useRef(null);
  const messagesRef = useRef([]);

  const toggleIsExpanded = useCallback(() => {
    const newValue = !isExpanded;
    setIsExpanded(newValue);
    onToggleExpander(newValue);
  }, [isExpanded, onToggleExpander]);

  const expanderButtonMessageDescriptor = isExpanded ? collapseMessageDescriptor : expandMessageDescriptor;

  const checkMessageHeight = useCallback(() => {
    if (messagesRef?.current) {
      const totalNumberOfLines = messagesRef.current.reduce((acc, messageElement) => {
        if (messageElement) {
          return acc + getNumberOfLinesInElement(messageElement);
        }

        return acc;
      }, 0);

      if (totalNumberOfLines > maxLines) {
        setShowExpanderButton(true);
      } else {
        setShowExpanderButton(false);
      }
    }
  }, [maxLines]);

  useLayoutEffect(checkMessageHeight, [checkMessageHeight, messages]);

  useOnElementResize(truncatedTextContainerRef, checkMessageHeight);

  // Despite the webkit prefix, this works in all major browsers.
  const messageContainerStyle = useMemo(() => {
    return isExpanded
      ? null
      : {
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: maxLines,
          WebkitBoxOrient: 'vertical',
        };
  }, [isExpanded, maxLines]);

  const updateMessagesRef = useCallback((messageIndex, messageRef) => {
    messagesRef.current[messageIndex] = messageRef;
  }, []);

  return (
    <div className={classnames(style['expandable-message-container'], className)}>
      <div ref={truncatedTextContainerRef} className={style['message-container']} style={messageContainerStyle}>
        {messages.map((message, index) => (
          <MessageText
            key={index}
            isBold={isBold}
            message={message}
            messageIndex={index}
            onUpdateRef={updateMessagesRef}
            textType={textType}
          />
        ))}
      </div>
      {showExpanderButton && (
        <Button className={classnames(style['expander-button'], buttonClassName)} onClick={toggleIsExpanded} theme='link'>
          <FormattedMessage {...expanderButtonMessageDescriptor} />
        </Button>
      )}
    </div>
  );
};

ExpandableMessage.propTypes = {
  messages: PropTypes.arrayOf(PropTypes.string).isRequired,
  maxLines: PropTypes.number.isRequired,
  collapseMessageDescriptor: MessageDescriptorPropType,
  expandMessageDescriptor: MessageDescriptorPropType,
  className: PropTypes.string,
  textType: TextTypePropType,
  onToggleExpander: PropTypes.func,
  buttonClassName: PropTypes.string,
  isBold: PropTypes.bool,
};
